<template>
  <div class="fixed inset-0 z-50 overflow-y-auto wizard-modal landingpage-wizard-modal">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity wizard-backdrop" @click="$emit('close')"></div>

      <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full wizard-dialog">
        <div class="bg-white px-6 pt-5 pb-4 wizard-body">
          <div class="flex items-center justify-between border-b border-gray-200 pb-4 mb-6">
            <div>
              <h3 class="text-lg font-medium text-gray-900">Landing Page Wizard</h3>
              <p class="text-sm text-gray-500 mt-1">Step {{ currentStep }} of {{ totalSteps }}: {{ stepTitles[currentStep - 1] }}</p>
            </div>
            <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600" aria-label="Close landing page wizard">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="mb-6">
            <div class="flex items-center">
              <div
                v-for="step in totalSteps"
                :key="step"
                class="flex items-center"
                :class="{ 'flex-1': step < totalSteps }"
              >
                <div
                  class="flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium"
                  :class="step <= currentStep ? 'bg-violet-600 border-violet-600 text-white' : 'border-gray-300 text-gray-500'"
                >
                  {{ step }}
                </div>
                <div
                  v-if="step < totalSteps"
                  class="flex-1 h-0.5 mx-4"
                  :class="step < currentStep ? 'bg-violet-600' : 'bg-gray-300'"
                ></div>
              </div>
            </div>
          </div>

          <div class="space-y-6">
            <div v-if="currentStep === 1">
              <h4 class="text-lg font-medium text-gray-900 mb-4">What should this landing page sell or explain?</h4>
              <div class="space-y-4">
                <div>
                  <label for="landing-offer" class="block text-sm font-medium text-gray-700 mb-1">Product, service, or offer *</label>
                  <input
                    id="landing-offer"
                    v-model="landingData.offer"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="e.g. AI-assisted website maintenance for SaaS teams"
                  />
                </div>
                <div>
                  <label for="landing-goal" class="block text-sm font-medium text-gray-700 mb-1">Primary conversion goal *</label>
                  <select
                    id="landing-goal"
                    v-model="landingData.conversionGoal"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  >
                    <option value="">Select a goal</option>
                    <option value="Book a call">Book a call</option>
                    <option value="Start a free trial">Start a free trial</option>
                    <option value="Request a demo">Request a demo</option>
                    <option value="Download a lead magnet">Download a lead magnet</option>
                    <option value="Join a waitlist">Join a waitlist</option>
                    <option value="Buy now">Buy now</option>
                  </select>
                </div>
                <div>
                  <label for="landing-cta" class="block text-sm font-medium text-gray-700 mb-1">Preferred CTA text</label>
                  <input
                    id="landing-cta"
                    v-model="landingData.cta"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="e.g. Book a strategy call"
                  />
                </div>
              </div>
            </div>

            <div v-if="currentStep === 2">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Audience and user intent</h4>
              <div class="space-y-4">
                <div>
                  <label for="landing-audience" class="block text-sm font-medium text-gray-700 mb-1">Target audience *</label>
                  <textarea
                    id="landing-audience"
                    v-model="landingData.audience"
                    rows="3"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="e.g. Seed-stage SaaS founders who need a high-converting site without hiring a full marketing team"
                  ></textarea>
                </div>
                <div>
                  <label for="landing-intent" class="block text-sm font-medium text-gray-700 mb-1">Search/user intent *</label>
                  <select
                    id="landing-intent"
                    v-model="landingData.userIntent"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  >
                    <option value="">Select intent</option>
                    <option value="Transactional">Transactional - ready to buy or book</option>
                    <option value="Commercial investigation">Commercial investigation - comparing options</option>
                    <option value="Informational">Informational - learning and evaluating</option>
                    <option value="Navigational">Navigational - looking for a specific brand or solution</option>
                  </select>
                </div>
                <div>
                  <label for="landing-pain-points" class="block text-sm font-medium text-gray-700 mb-1">Pain points or objections</label>
                  <textarea
                    id="landing-pain-points"
                    v-model="landingData.painPoints"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="e.g. slow redesign cycles, unclear SEO outcomes, no internal web team"
                  ></textarea>
                </div>
              </div>
            </div>

            <div v-if="currentStep === 3">
              <h4 class="text-lg font-medium text-gray-900 mb-4">SEO, AEO, and GEO tags</h4>
              <div class="space-y-4">
                <div>
                  <label for="landing-seo-keywords" class="block text-sm font-medium text-gray-700 mb-1">SEO keywords *</label>
                  <textarea
                    id="landing-seo-keywords"
                    v-model="landingData.seoKeywords"
                    rows="3"
                    required
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="website builder, AI website maintenance, SEO website creation"
                  ></textarea>
                  <p class="text-xs text-gray-500 mt-1">Comma-separated keywords or search phrases.</p>
                </div>
                <div>
                  <label for="landing-aeo-questions" class="block text-sm font-medium text-gray-700 mb-1">AEO questions to answer</label>
                  <textarea
                    id="landing-aeo-questions"
                    v-model="landingData.aeoQuestions"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="What is AI website maintenance? How does this improve SEO? Who is it for?"
                  ></textarea>
                  <p class="text-xs text-gray-500 mt-1">Questions should be answered directly on the page for answer engines and snippets.</p>
                </div>
                <div>
                  <label for="landing-geo-tags" class="block text-sm font-medium text-gray-700 mb-1">GEO citation tags or authority signals</label>
                  <textarea
                    id="landing-geo-tags"
                    v-model="landingData.geoTags"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="industry, audience, use case, differentiators, claims that should be citable"
                  ></textarea>
                  <p class="text-xs text-gray-500 mt-1">Used to shape concise, citable statements for generative search results.</p>
                </div>
              </div>
            </div>

            <div v-if="currentStep === 4">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Content direction</h4>
              <div class="space-y-4">
                <div>
                  <label for="landing-tone" class="block text-sm font-medium text-gray-700 mb-1">Tone</label>
                  <select
                    id="landing-tone"
                    v-model="landingData.tone"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  >
                    <option value="clear and conversion-focused">Clear and conversion-focused</option>
                    <option value="premium and authoritative">Premium and authoritative</option>
                    <option value="friendly and practical">Friendly and practical</option>
                    <option value="technical and precise">Technical and precise</option>
                    <option value="bold and punchy">Bold and punchy</option>
                  </select>
                </div>
                <div>
                  <label for="landing-proof" class="block text-sm font-medium text-gray-700 mb-1">Proof points, benefits, or differentiators</label>
                  <textarea
                    id="landing-proof"
                    v-model="landingData.proofPoints"
                    rows="4"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="e.g. faster launch cycles, SEO-first implementation, source-level edits, measurable web improvements"
                  ></textarea>
                </div>
                <div>
                  <label for="landing-notes" class="block text-sm font-medium text-gray-700 mb-1">Anything to avoid or include</label>
                  <textarea
                    id="landing-notes"
                    v-model="landingData.additionalNotes"
                    rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="e.g. avoid generic SaaS copy, include comparison section, use existing brand voice"
                  ></textarea>
                </div>
              </div>
            </div>

            <div v-if="currentStep === 5">
              <h4 class="text-lg font-medium text-gray-900 mb-4">Review landing page brief</h4>
              <div class="bg-gray-50 rounded-lg p-4">
                <div class="text-xs text-gray-600 space-y-2">
                  <div><strong>Offer:</strong> {{ landingData.offer }}</div>
                  <div><strong>Goal:</strong> {{ landingData.conversionGoal }}</div>
                  <div><strong>Audience:</strong> {{ landingData.audience }}</div>
                  <div><strong>User intent:</strong> {{ landingData.userIntent }}</div>
                  <div><strong>SEO keywords:</strong> {{ landingData.seoKeywords }}</div>
                  <div v-if="landingData.aeoQuestions"><strong>AEO questions:</strong> {{ landingData.aeoQuestions }}</div>
                  <div v-if="landingData.geoTags"><strong>GEO tags:</strong> {{ landingData.geoTags }}</div>
                </div>
              </div>
              <p class="text-xs text-gray-500 mt-3">
                The AI will inspect the existing website source and create the page in the current site's visual style.
              </p>
            </div>
          </div>
        </div>

        <div v-if="submitError" class="px-6 py-2 text-sm text-red-400 bg-red-900/20 border-t border-red-500/20">
          {{ submitError }}
        </div>

        <div class="bg-gray-50 px-6 py-3 flex items-center justify-between">
          <button
            v-if="currentStep > 1"
            @click="previousStep"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div v-else></div>

          <div class="space-x-3">
            <button
              @click="$emit('close')"
              class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              v-if="currentStep < totalSteps"
              @click="nextStep"
              :disabled="!canProceed"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <button
              v-else
              @click="createLandingPage"
              :disabled="!canProceed || isSubmitting"
              class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isSubmitting" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              {{ isSubmitting ? 'Creating...' : 'Create Landing Page' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, reactive, ref } from 'vue'

export default {
  name: 'LandingPageWizardModal',
  emits: ['close', 'create'],
  setup(props, { emit }) {
    const currentStep = ref(1)
    const totalSteps = 5
    const isSubmitting = ref(false)
    const submitError = ref('')
    const stepTitles = [
      'Offer',
      'Audience Intent',
      'SEO AEO GEO',
      'Content Direction',
      'Review'
    ]

    const landingData = reactive({
      offer: '',
      conversionGoal: '',
      cta: '',
      audience: '',
      userIntent: '',
      painPoints: '',
      seoKeywords: '',
      aeoQuestions: '',
      geoTags: '',
      tone: 'clear and conversion-focused',
      proofPoints: '',
      additionalNotes: ''
    })

    const canProceed = computed(() => {
      switch (currentStep.value) {
        case 1:
          return landingData.offer.trim() !== '' && landingData.conversionGoal !== ''
        case 2:
          return landingData.audience.trim() !== '' && landingData.userIntent !== ''
        case 3:
          return landingData.seoKeywords.trim() !== ''
        case 4:
        case 5:
          return true
        default:
          return false
      }
    })

    const nextStep = () => {
      if (currentStep.value < totalSteps && canProceed.value) {
        currentStep.value++
      }
    }

    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }

    const createLandingPage = () => {
      if (!canProceed.value || isSubmitting.value) return
      isSubmitting.value = true
      submitError.value = ''

      const prompt = `Create a high-converting landing page for the existing website.

LANDING PAGE BRIEF
- Product/service/offer: ${landingData.offer}
- Primary conversion goal: ${landingData.conversionGoal}
- Preferred CTA text: ${landingData.cta || landingData.conversionGoal}
- Target audience: ${landingData.audience}
- User/search intent: ${landingData.userIntent}
- Pain points or objections: ${landingData.painPoints || 'Infer from the offer and audience.'}
- Tone: ${landingData.tone}
- Proof points, benefits, or differentiators: ${landingData.proofPoints || 'Infer reasonable proof points from the existing site context.'}
- Additional notes: ${landingData.additionalNotes || 'None.'}

SEO REQUIREMENTS
- Target keywords/tags: ${landingData.seoKeywords}
- Use the keywords naturally in the page title, H1, H2s, meta description, intro copy, CTA-adjacent copy, and FAQ where appropriate.
- Optimize for the selected user intent: ${landingData.userIntent}. The page should satisfy what this visitor is trying to do next.

AEO REQUIREMENTS
- Questions to answer directly: ${landingData.aeoQuestions || 'Infer the highest-intent questions for this offer and audience.'}
- Include concise answer-style sections or FAQ entries that can work for featured snippets and answer engines.
- Put the clearest answer near the relevant heading, not buried in long copy.

GEO REQUIREMENTS
- Citation/authority tags: ${landingData.geoTags || 'Use the offer, audience, category, differentiators, and measurable claims as GEO tags.'}
- Include short, citable statements that generative search engines can quote without losing context.
- Avoid unsupported statistics. If no source exists in the repo, use qualitative claims instead of fabricated numbers.

PAGE STRUCTURE
1. Hero section with a clear H1, specific value proposition, and primary CTA.
2. Problem and audience-fit section aligned to the user intent.
3. Benefits/features section tied to outcomes, not generic feature lists.
4. Proof or trust section using existing site context where available.
5. SEO/AEO-friendly FAQ or answer section.
6. Final CTA section.

IMPLEMENTATION REQUIREMENTS
1. Inspect the existing website source before editing, especially pages/*.vue, components, layouts, CSS assets, and configuration.
2. Add the landing page in the current website's native visual style. Match typography, spacing, colors, section treatments, cards, buttons, borders, and responsive behavior.
3. Reuse existing components, CSS variables, utility classes, and layout patterns where possible.
4. Do not introduce a generic template style.
5. Do not install dependencies.
6. Do not modify unrelated pages or global styles unless strictly required for this landing page to render correctly.
7. Ensure the page is mobile responsive and works in Nuxt.`

      emit('create', {
        prompt,
        landingData: { ...landingData },
        onError: (msg) => {
          isSubmitting.value = false
          submitError.value = msg || 'Failed to create landing page. Please try again.'
        }
      })
    }

    return {
      currentStep,
      totalSteps,
      stepTitles,
      landingData,
      canProceed,
      isSubmitting,
      submitError,
      nextStep,
      previousStep,
      createLandingPage
    }
  }
}
</script>

<style src="./wizard-glass-modal.css"></style>

